import {
  Box,
  Card,
  Flex,
  FormControl,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import CommonInput from 'components/commonInput/CommonInput';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import CommonSubmitBtn from 'components/commonInput/CommonSubmitBtn';
import CommonSwitchInput from 'components/commonInput/CommonSwitchInput';
import React, { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import { payoutSettingCreateValidation } from 'utils/CommonValidation';
import {
  GetPayoutSettingById,
  GetStripeAccounts,
  UpdatePayoutSetting,
} from 'reactRedux/thunks/payoutSettingThunk/payoutSettingThunk';
import PageLoader from 'components/wholePageLoader/PageLoader';

const PAYOUT_FREQUENCY_OPTIONS = [
  { id: 'weekly', label: 'Weekly' },
  { id: 'monthly', label: 'Monthly' },
];

const DAY_OF_WEEK_OPTIONS = [
  { id: 1, label: 'Monday' },
  { id: 2, label: 'Tuesday' },
  { id: 3, label: 'Wednesday' },
  { id: 4, label: 'Thursday' },
  { id: 5, label: 'Friday' },
  { id: 6, label: 'Saturday' },
  { id: 7, label: 'Sunday' },
];

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  id: i + 1,
  label: `${i + 1}`,
}));

const getNext3Settlements = (frequency, day, time) => {
  if (!frequency || !day || !time) return [];
  const parts = time.split(':');
  if (parts.length !== 2) return [];
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);
  if (isNaN(hour) || isNaN(minute)) return [];

  const results = [];
  const now = new Date();

  if (frequency === 'weekly') {
    const targetIsoDay = parseInt(day, 10);
    let cursor = new Date(now);
    while (results.length < 3) {
      const curIsoDay = cursor.getUTCDay() === 0 ? 7 : cursor.getUTCDay();
      const daysUntil = (targetIsoDay - curIsoDay + 7) % 7;
      cursor = new Date(
        Date.UTC(
          cursor.getUTCFullYear(),
          cursor.getUTCMonth(),
          cursor.getUTCDate() + daysUntil,
          hour,
          minute,
          0,
          0,
        ),
      );
      if (cursor > now) results.push(new Date(cursor));
      const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
      cursor = new Date(cursor.getTime() + oneWeekMs);
    }
  } else if (frequency === 'monthly') {
    const targetDay = parseInt(day, 10);
    let year = now.getUTCFullYear();
    let month = now.getUTCMonth();
    while (results.length < 3) {
      const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      const d = Math.min(targetDay, daysInMonth);
      const candidate = new Date(Date.UTC(year, month, d, hour, minute, 0, 0));
      if (candidate > now) results.push(candidate);
      month += 1;
      if (month > 11) {
        month = 0;
        year += 1;
      }
    }
  }

  return results;
};

const formatSettlementDate = (date) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const day = days[date.getUTCDay()];
  const d = String(date.getUTCDate()).padStart(2, '0');
  const mon = months[date.getUTCMonth()];
  const yr = date.getUTCFullYear();
  const hh = String(date.getUTCHours()).padStart(2, '0');
  const mm = String(date.getUTCMinutes()).padStart(2, '0');
  return `${day}, ${d} ${mon} ${yr} ${hh}:${mm} UTC`;
};

const EditPayoutSetting = () => {
  const { payoutSettingEditId } = useParams();
  const { textColor, brandStars } = useColorModeVariables();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    isLoading,
    isViewLoading,
    isStripeAccountsLoading,
    stripeAccounts,
    viewPayoutSetting,
  } = useSelector((state) => state.PayoutSettingSlice);

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(payoutSettingCreateValidation),
    defaultValues: { isActive: true },
  });

  const selectedConnectedAccount = watch('connectedAccount');
  const payoutFrequency = watch('payoutFrequency');
  const scheduledDayOfWeek = watch('scheduledDayOfWeek');
  const scheduledDayOfMonth = watch('scheduledDayOfMonth');
  const scheduledTime = watch('scheduledTime');

  const isWeekly = payoutFrequency?.id === 'weekly';
  const isMonthly = payoutFrequency?.id === 'monthly';

  const connectedAccountOptions = stripeAccounts.connectedAccounts.map((a) => ({
    id: a.id,
    label: a.name,
    country: a.country,
  }));

  const next3Settlements = useMemo(() => {
    const freq = payoutFrequency?.id;
    const day = isWeekly
      ? scheduledDayOfWeek?.id
      : isMonthly
        ? scheduledDayOfMonth?.id
        : null;
    return getNext3Settlements(freq, day, scheduledTime);
  }, [
    payoutFrequency,
    scheduledDayOfWeek,
    scheduledDayOfMonth,
    scheduledTime,
    isWeekly,
    isMonthly,
  ]);

  // Load setting and stripe accounts on mount
  useEffect(() => {
    dispatch(GetPayoutSettingById(payoutSettingEditId));
    dispatch(GetStripeAccounts());
  }, [dispatch, payoutSettingEditId]);

  // Pre-fill form once both setting and accounts are loaded
  useEffect(() => {
    if (!viewPayoutSetting || connectedAccountOptions.length === 0) return;

    const matchedAccount = connectedAccountOptions.find(
      (a) => a.id === viewPayoutSetting.connectedAccountId,
    ) || {
      id: viewPayoutSetting.connectedAccountId,
      label: viewPayoutSetting.connectedAccountName,
      country: viewPayoutSetting.connectedAccountCountry,
    };

    const freqOption = PAYOUT_FREQUENCY_OPTIONS.find(
      (f) => f.id === viewPayoutSetting.payoutFrequency,
    );
    const dowOption = viewPayoutSetting.scheduledDayOfWeek
      ? DAY_OF_WEEK_OPTIONS.find(
          (d) => d.id === viewPayoutSetting.scheduledDayOfWeek,
        )
      : null;
    const domOption = viewPayoutSetting.scheduledDayOfMonth
      ? DAY_OF_MONTH_OPTIONS.find(
          (d) => d.id === viewPayoutSetting.scheduledDayOfMonth,
        )
      : null;

    reset({
      connectedAccount: matchedAccount,
      connectedAccountId: viewPayoutSetting.connectedAccountId,
      connectedAccountCountry: viewPayoutSetting.connectedAccountCountry,
      revenueSplitRatio: viewPayoutSetting.revenueSplitRatio,
      payoutFrequency: freqOption || null,
      scheduledDayOfWeek: dowOption || null,
      scheduledDayOfMonth: domOption || null,
      scheduledTime: viewPayoutSetting.scheduledTime || '',
      isActive: viewPayoutSetting.isActive,
    });
  }, [viewPayoutSetting, stripeAccounts, reset]); // eslint-disable-line

  // Auto-fill ID and country when account selection changes
  useEffect(() => {
    if (selectedConnectedAccount?.id) {
      setValue('connectedAccountId', selectedConnectedAccount.id);
      setValue(
        'connectedAccountCountry',
        selectedConnectedAccount.country || '',
      );
    }
  }, [selectedConnectedAccount, setValue]);

  const onSubmit = (data) => {
    const payload = {
      connectedAccountName: data.connectedAccount.label,
      connectedAccountId: data.connectedAccount.id,
      connectedAccountCountry: data.connectedAccount.country || '',
      revenueSplitRatio: Number(data.revenueSplitRatio),
      payoutFrequency: data.payoutFrequency.id,
      scheduledTime: data.scheduledTime,
      isActive: !!data.isActive,
      scheduledDayOfWeek:
        data.payoutFrequency.id === 'weekly'
          ? data.scheduledDayOfWeek?.id
          : null,
      scheduledDayOfMonth:
        data.payoutFrequency.id === 'monthly'
          ? data.scheduledDayOfMonth?.id
          : null,
    };

    dispatch(
      UpdatePayoutSetting(payoutSettingEditId, payload, () => {
        navigate(`/admin/payout-settings/${payoutSettingEditId}`);
      }),
    );
  };

  if (isViewLoading) return <PageLoader />;

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      <Flex direction="column" align="center" justify="center">
        <Card
          w="100%"
          boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
          borderRadius="20px"
          paddingInline="40px"
          paddingBlock="50px"
          overflowX={{ sm: 'scroll', lg: 'hidden' }}
        >
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <FormControl>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="24px">
                <Controller
                  name="connectedAccount"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      label="Connected Account"
                      name="connectedAccount"
                      type="select"
                      options={connectedAccountOptions}
                      value={value}
                      onChange={onChange}
                      register={register}
                      isRequired={true}
                      error={errors?.connectedAccount}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.id}
                      isLoading={isStripeAccountsLoading}
                    />
                  )}
                />

                <CommonInput
                  type="text"
                  name="connectedAccountId"
                  labelText="Connected Account ID"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Auto-filled"
                  register={register('connectedAccountId')}
                  errors={errors?.connectedAccountId}
                  isDisabled={true}
                />

                <CommonInput
                  type="text"
                  name="connectedAccountCountry"
                  labelText="Connected Account Country"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="Auto-filled"
                  register={register('connectedAccountCountry')}
                  errors={errors?.connectedAccountCountry}
                  isDisabled={true}
                />

                <CommonInput
                  type="number"
                  name="revenueSplitRatio"
                  labelText="Revenue Split Ratio (%)"
                  textColor={textColor}
                  brandStars={brandStars}
                  placeholder="e.g. 70"
                  register={register('revenueSplitRatio')}
                  errors={errors?.revenueSplitRatio}
                />

                <Controller
                  name="payoutFrequency"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <CommonReactSelect
                      label="Payout Frequency"
                      name="payoutFrequency"
                      type="select"
                      options={PAYOUT_FREQUENCY_OPTIONS}
                      value={value}
                      onChange={onChange}
                      register={register}
                      isRequired={true}
                      error={errors?.payoutFrequency}
                      getOptionLabel={(e) => e?.label}
                      getOptionValue={(e) => e?.id}
                    />
                  )}
                />

                {isWeekly && (
                  <Controller
                    name="scheduledDayOfWeek"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CommonReactSelect
                        label="Day of Week"
                        name="scheduledDayOfWeek"
                        type="select"
                        options={DAY_OF_WEEK_OPTIONS}
                        value={value}
                        onChange={onChange}
                        register={register}
                        isRequired={true}
                        error={errors?.scheduledDayOfWeek}
                        getOptionLabel={(e) => e?.label}
                        getOptionValue={(e) => e?.id}
                      />
                    )}
                  />
                )}

                {isMonthly && (
                  <Controller
                    name="scheduledDayOfMonth"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <CommonReactSelect
                        label="Day of Month"
                        name="scheduledDayOfMonth"
                        type="select"
                        options={DAY_OF_MONTH_OPTIONS}
                        value={value}
                        onChange={onChange}
                        register={register}
                        isRequired={true}
                        error={errors?.scheduledDayOfMonth}
                        getOptionLabel={(e) => e?.label}
                        getOptionValue={(e) => e?.id}
                      />
                    )}
                  />
                )}

                {(isWeekly || isMonthly) && (
                  <CommonInput
                    type="time"
                    name="scheduledTime"
                    labelText="Settlement Time (UTC)"
                    textColor={textColor}
                    brandStars={brandStars}
                    register={register('scheduledTime')}
                    errors={errors?.scheduledTime}
                    isRequired={true}
                  />
                )}
              </SimpleGrid>

              {next3Settlements.length === 3 && (
                <Box
                  mt="24px"
                  p="16px"
                  borderRadius="12px"
                  bg="orange.50"
                  _dark={{ bg: 'whiteAlpha.100' }}
                  borderWidth="1px"
                  borderColor="orange.200"
                >
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    color="reviveOrange"
                    mb="8px"
                    fontFamily="Montserrat"
                  >
                    Next 3 Settlement Dates
                  </Text>
                  {next3Settlements.map((date, idx) => (
                    <Text
                      key={idx}
                      fontSize="sm"
                      color={textColor}
                      fontFamily="Montserrat"
                      mb="4px"
                    >
                      {`${idx + 1}. ${formatSettlementDate(date)}`}
                    </Text>
                  ))}
                </Box>
              )}

              <Box mt="24px">
                <CommonSwitchInput
                  labelText="Enable Auto Payout"
                  name="isActive"
                  control={control}
                  register={register('isActive')}
                  errors={errors?.isActive}
                  textColor={textColor}
                  brandStars={brandStars}
                />
              </Box>

              <SimpleGrid
                columns={{ base: 1, md: 2, lg: 3 }}
                spacing="24px"
                mt="24px"
              >
                <CommonSubmitBtn
                  BTNtext="Update Payout Setting"
                  isLoading={isLoading}
                />
              </SimpleGrid>
            </FormControl>
          </form>
        </Card>
      </Flex>
    </Box>
  );
};

export default EditPayoutSetting;
